import type { FC } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import styled from "styled-components";
import type { TimeBucket } from "../../../shared/issuance_utils.ts";

export interface IssuanceTimelineChartProps {
  data: TimeBucket[];
  onBarClick?: (bucket: TimeBucket) => void;
}

const ChartWrapper = styled.div`
  width: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyState = styled.p`
  font-family: var(--vmx-font);
  font-size: 0.875rem;
  color: var(--vmx-gray-500);
  text-align: center;
`;

export const IssuanceTimelineChart: FC<IssuanceTimelineChartProps> = ({
  data,
  onBarClick,
}) => {
  const totalCount = data.reduce((sum, b) => sum + b.count, 0);

  if (data.length === 0 || totalCount === 0) {
    return (
      <ChartWrapper>
        <EmptyState>No certificates issued in this period</EmptyState>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={20} />
          <Tooltip
            cursor={false}
            formatter={(value: number) => [value, "Certificates"]}
            labelFormatter={(label: string) => label}
          />
          <Bar
            dataKey="count"
            fill="var(--vmx-accent, #00b4d8)"
            cursor={onBarClick ? "pointer" : undefined}
            onClick={(_: unknown, index: number) => {
              const bucket = data[index];
              if (bucket && onBarClick) onBarClick(bucket);
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};
